import React, { useEffect, useId, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  Button,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  getTacitsBalance,
  getCreditPackages,
  purchaseTacits,
  getCreditHistory,
  TacitsBalance,
  CreditPackage,
  TacitsCreditTransaction,
} from "../../api/pricing";
import { useGlobalTheme } from "../../context/GlobalThemeContext";

function formatNumber(n: number): string {
  return Math.round(n).toLocaleString("pt-BR");
}

// ── Coin SVG ─────────────────────────────────────────────────────────────────
function CoinIcon({
  size = 28,
  glow = false,
}: {
  size?: number;
  glow?: boolean;
}) {
  const id = useId().replace(/:/g, "");
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      style={{
        filter: glow ? "drop-shadow(0 0 6px rgba(247,216,74,0.7))" : undefined,
        flexShrink: 0,
      }}
    >
      <defs>
        <radialGradient id={`cg_${id}`} cx="38%" cy="32%" r="68%">
          <stop offset="0%" stopColor="#fde68a" />
          <stop offset="55%" stopColor="#f7d84a" />
          <stop offset="100%" stopColor="#b87800" />
        </radialGradient>
        <radialGradient id={`ch_${id}`} cx="35%" cy="28%" r="45%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
      </defs>
      <circle
        cx="16"
        cy="16"
        r="15"
        fill={`url(#cg_${id})`}
        stroke="#8a5a00"
        strokeWidth="0.8"
      />
      <circle cx="16" cy="16" r="15" fill={`url(#ch_${id})`} />
      <text
        x="16"
        y="21"
        textAnchor="middle"
        fontSize="13"
        fontWeight="800"
        fill="#5a3400"
        fontFamily="Arial,sans-serif"
      >
        T
      </text>
    </svg>
  );
}

// ── Type icon chips ───────────────────────────────────────────────────────────
function TypeBadge({ type, isDark }: { type: string; isDark: boolean }) {
  const cfg: Record<string, { label: string; bg: string; color: string }> = {
    signup_bonus: {
      label: "Bônus",
      bg: isDark ? "rgba(74,222,128,0.15)" : "rgba(22,163,74,0.10)",
      color: isDark ? "#4ade80" : "#16a34a",
    },
    purchase: {
      label: "Compra",
      bg: isDark ? "rgba(251,191,36,0.15)" : "rgba(234,179,8,0.10)",
      color: isDark ? "#fbbf24" : "#92600a",
    },
    manual: {
      label: "Manual",
      bg: isDark ? "rgba(167,139,250,0.15)" : "rgba(109,40,217,0.08)",
      color: isDark ? "#a78bfa" : "#6d28d9",
    },
  };
  const c = cfg[type] ?? {
    label: type,
    bg: "transparent",
    color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
  };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.2,
        py: 0.35,
        borderRadius: "999px",
        backgroundColor: c.bg,
        color: c.color,
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
      }}
    >
      {c.label}
    </Box>
  );
}

// ── Package card ──────────────────────────────────────────────────────────────
interface PackageCardProps {
  pkg: CreditPackage;
  isDark: boolean;
  purchasing: boolean;
  onBuy: () => void;
  t: ReturnType<typeof makeTheme>;
}

function PackageCard({ pkg, isDark, purchasing, onBuy, t }: PackageCardProps) {
  const popular = pkg.popular;
  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: "18px",
        p: 0,
        overflow: "hidden",
        border: `1.5px solid ${popular ? (isDark ? "rgba(247,216,74,0.45)" : "rgba(234,179,8,0.55)") : t.border}`,
        background: popular
          ? isDark
            ? "linear-gradient(160deg,rgba(251,191,36,0.14) 0%,rgba(20,20,20,0.98) 60%)"
            : "linear-gradient(160deg,rgba(251,191,36,0.13) 0%,#fff 60%)"
          : t.surface,
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.18s, box-shadow 0.18s",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: popular
            ? `0 12px 32px rgba(247,216,74,${isDark ? "0.18" : "0.22"})`
            : `0 8px 24px rgba(0,0,0,${isDark ? "0.28" : "0.10"})`,
        },
      }}
    >
      {/* Top accent strip */}
      <Box
        sx={{
          height: 4,
          background: popular
            ? "linear-gradient(90deg,#f7d84a,#e8a800)"
            : isDark
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.05)",
        }}
      />

      {/* Popular badge */}
      {popular && (
        <Box sx={{ position: "absolute", top: 14, right: 12 }}>
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: "999px",
              background: "linear-gradient(90deg,#f7d84a,#e8a800)",
              color: "#5a3400",
              fontSize: "0.62rem",
              fontWeight: 800,
              letterSpacing: "0.05em",
              whiteSpace: "nowrap",
            }}
          >
            ★ POPULAR
          </Box>
        </Box>
      )}

      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Label */}
        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: popular ? (isDark ? "#fbbf24" : "#92600a") : t.textMuted,
            mb: 1.5,
          }}
        >
          {pkg.label}
        </Typography>

        {/* Tacits count */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <CoinIcon size={22} glow={popular} />
          <Typography
            sx={{
              fontSize: "1.9rem",
              fontWeight: 900,
              lineHeight: 1,
              color: popular ? (isDark ? "#fde68a" : "#92600a") : t.textPrimary,
              letterSpacing: "-0.03em",
            }}
          >
            {formatNumber(pkg.tacits)}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: "0.7rem",
            color: t.textMuted,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            mb: 2.5,
            pl: "30px",
          }}
        >
          tacits
        </Typography>

        {/* Price */}
        <Box sx={{ mt: "auto" }}>
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: t.textMuted,
              fontWeight: 600,
              mb: 0.25,
            }}
          >
            por apenas
          </Typography>
          <Typography
            sx={{
              fontSize: "1.4rem",
              fontWeight: 900,
              color: t.textPrimary,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
              mb: 2,
            }}
          >
            U${" "}
            <span style={{ fontSize: "1.8rem" }}>
              {pkg.price_usd.toFixed(2)}
            </span>
          </Typography>

          <Button
            fullWidth
            size="small"
            variant="contained"
            disabled={purchasing}
            onClick={onBuy}
            disableElevation
            sx={{
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "0.78rem",
              py: 1,
              letterSpacing: "0.02em",
              background: popular
                ? "linear-gradient(90deg,#f7d84a,#e8a800)"
                : isDark
                  ? "rgba(167,139,250,0.18)"
                  : "rgba(109,40,217,0.10)",
              color: popular ? "#5a3400" : isDark ? "#c4b5fd" : "#6d28d9",
              "&:hover": {
                background: popular
                  ? "linear-gradient(90deg,#fde68a,#f7d84a)"
                  : isDark
                    ? "rgba(167,139,250,0.28)"
                    : "rgba(109,40,217,0.18)",
              },
              "&:disabled": { opacity: 0.55 },
            }}
          >
            {purchasing ? "Processando…" : "Comprar"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

// ── Theme factory ─────────────────────────────────────────────────────────────
function makeTheme(isDark: boolean) {
  return {
    surface: isDark ? "rgba(20,20,20,0.98)" : "#ffffff",
    surfaceAlt: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.015)",
    border: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.09)",
    textPrimary: isDark ? "#ffffff" : "#1a1a2e",
    textMuted: isDark ? "rgba(255,255,255,0.38)" : "rgba(0,0,0,0.42)",
    skeletonBg: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.07)",
    planBg: isDark ? "rgba(139,92,246,0.15)" : "rgba(96,191,183,0.12)",
    planColor: isDark ? "#a78bfa" : "hsl(170,50%,35%)",
    goldAccent: isDark ? "hsl(45,100%,55%)" : "hsl(45,80%,33%)",
    greenAccent: isDark ? "#4ade80" : "#16a34a",
    blueAccent: isDark ? "#a78bfa" : "#6d28d9",
  };
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function CreditsPage() {
  const isDark = useGlobalTheme();
  const t = makeTheme(isDark);

  const [balance, setBalance] = useState<TacitsBalance | null>(null);
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [history, setHistory] = useState<TacitsCreditTransaction[]>([]);

  const [loadingBalance, setLoadingBalance] = useState(true);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    getTacitsBalance()
      .then(setBalance)
      .catch(() => setBalance(null))
      .finally(() => setLoadingBalance(false));

    getCreditPackages()
      .then(setPackages)
      .catch(() => setPackages([]))
      .finally(() => setLoadingPackages(false));

    getCreditHistory()
      .then(setHistory)
      .catch(() => setHistory([]))
      .finally(() => setLoadingHistory(false));
  }, []);

  async function handlePurchase(packageId: string) {
    setPurchasingId(packageId);
    try {
      const result = await purchaseTacits(
        packageId,
        window.location.href + "?credits=success",
        window.location.href,
      );
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch {
      setErrorMsg("Erro ao processar a compra. Tente novamente.");
    } finally {
      setPurchasingId(null);
    }
  }

  function getTypeLabel(type: string): string {
    switch (type) {
      case "signup_bonus":
        return "Bônus de cadastro";
      case "purchase":
        return "Compra";
      case "manual":
        return "Manual";
      default:
        return type;
    }
  }

  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: "auto",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 4 },
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 0.75 }}
          >
            <CoinIcon size={30} glow />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: t.textPrimary,
                letterSpacing: "-0.02em",
              }}
            >
              Comprar Créditos
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: t.textMuted, pl: "42px" }}>
            Expanda seu saldo além do plano mensal — créditos não expiram
          </Typography>
        </Box>

        {/* Plan badge */}
        {loadingBalance ? (
          <Skeleton
            variant="rounded"
            width={80}
            height={28}
            sx={{ borderRadius: "999px", bgcolor: t.skeletonBg, flexShrink: 0 }}
          />
        ) : balance?.plan ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.6,
              borderRadius: "999px",
              backgroundColor: t.planBg,
              border: `1px solid ${t.planColor}44`,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: t.planColor,
              }}
            />
            <Typography
              sx={{
                color: t.planColor,
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
              }}
            >
              {balance.plan}
            </Typography>
          </Box>
        ) : null}
      </Box>

      {/* ── Balance hero card ────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          borderRadius: "20px",
          border: `1px solid ${t.border}`,
          overflow: "hidden",
          background: t.surface,
        }}
      >
        {/* Subtle top gradient bar */}
        <Box
          sx={{
            height: 3,
            background:
              "linear-gradient(90deg,#a78bfa 0%,#f7d84a 50%,#4ade80 100%)",
            opacity: 0.7,
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            px: { xs: 2.5, sm: 4 },
            py: { xs: 2.5, sm: 3 },
            gap: { xs: 3, sm: 0 },
          }}
        >
          {loadingBalance ? (
            <Box
              sx={{
                display: "flex",
                gap: 3,
                flex: 1,
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              {[1, 2, 3].map((i) => (
                <Box key={i} sx={{ flex: 1, minWidth: { xs: "40%", sm: 0 } }}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={14}
                    sx={{ bgcolor: t.skeletonBg, mb: 0.5 }}
                  />
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={36}
                    sx={{ bgcolor: t.skeletonBg }}
                  />
                </Box>
              ))}
            </Box>
          ) : balance ? (
            <>
              {/* Metric: Plano */}
              <Box
                sx={{
                  flex: 1,
                  pr: { sm: 4 },
                  borderRight: { sm: `1px solid ${t.border}` },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: t.textMuted,
                    mb: 0.5,
                  }}
                >
                  Do Plano
                </Typography>
                <Typography
                  sx={{
                    fontSize: "2rem",
                    fontWeight: 900,
                    color: t.blueAccent,
                    lineHeight: 1,
                    letterSpacing: "-0.03em",
                  }}
                >
                  {formatNumber(balance.allocated)}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.68rem", color: t.textMuted, mt: 0.3 }}
                >
                  tacits/mês
                </Typography>
              </Box>

              {/* Metric: Extras */}
              <Box
                sx={{
                  flex: 1,
                  px: { sm: 4 },
                  borderRight: { sm: `1px solid ${t.border}` },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: t.textMuted,
                    mb: 0.5,
                  }}
                >
                  Créditos Extras
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "2rem",
                      fontWeight: 900,
                      color: t.goldAccent,
                      lineHeight: 1,
                      letterSpacing: "-0.03em",
                    }}
                  >
                    {formatNumber(balance.manual_credits)}
                  </Typography>
                  {balance.manual_credits > 0 && (
                    <Box
                      sx={{
                        px: 0.8,
                        py: 0.2,
                        borderRadius: "6px",
                        background: isDark
                          ? "rgba(247,216,74,0.12)"
                          : "rgba(234,179,8,0.10)",
                        alignSelf: "flex-end",
                        mb: "2px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          fontWeight: 800,
                          color: t.goldAccent,
                          letterSpacing: "0.04em",
                        }}
                      >
                        +EXTRA
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography
                  sx={{ fontSize: "0.68rem", color: t.textMuted, mt: 0.3 }}
                >
                  não expiram
                </Typography>
              </Box>

              {/* Metric: Total */}
              <Box sx={{ flex: 1, pl: { sm: 4 } }}>
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: t.textMuted,
                    mb: 0.5,
                  }}
                >
                  Total Disponível
                </Typography>
                <Typography
                  sx={{
                    fontSize: "2rem",
                    fontWeight: 900,
                    color: t.greenAccent,
                    lineHeight: 1,
                    letterSpacing: "-0.03em",
                  }}
                >
                  {formatNumber(balance.remaining)}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.68rem", color: t.textMuted, mt: 0.3 }}
                >
                  tacits restantes
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: t.textMuted }}>
              Não foi possível carregar o saldo.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* ── Packages ─────────────────────────────────────────────────────────── */}
      <Box sx={{ mb: 5 }}>
        <Box
          sx={{ mb: 2.5, display: "flex", alignItems: "baseline", gap: 1.5 }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: t.textPrimary,
              letterSpacing: "-0.02em",
            }}
          >
            Adquirir Tacits
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: t.textMuted }}>
            Escolha um pacote
          </Typography>
        </Box>

        {loadingPackages ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2,1fr)", sm: "repeat(4,1fr)" },
              gap: 1.5,
            }}
          >
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={200}
                sx={{ borderRadius: "18px", bgcolor: t.skeletonBg }}
              />
            ))}
          </Box>
        ) : packages.length === 0 ? (
          <Typography variant="body2" sx={{ color: t.textMuted }}>
            Nenhum pacote disponível no momento.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2,1fr)", sm: "repeat(4,1fr)" },
              gap: 1.5,
            }}
          >
            {packages.map((pkg) => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                isDark={isDark}
                purchasing={purchasingId === pkg.id}
                onBuy={() => handlePurchase(pkg.id)}
                t={t}
              />
            ))}
          </Box>
        )}

        {/* Trust note */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: 2,
            opacity: 0.6,
          }}
        >
          <Box component="span" sx={{ fontSize: "0.8rem" }}>
            🔒
          </Box>
          <Typography sx={{ fontSize: "0.7rem", color: t.textMuted }}>
            Pagamento seguro via Stripe — créditos liberados imediatamente após
            a confirmação
          </Typography>
        </Box>
      </Box>

      {/* ── Credit History ───────────────────────────────────────────────────── */}
      <Box>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: t.textPrimary,
              letterSpacing: "-0.02em",
            }}
          >
            Histórico
          </Typography>
          {!loadingHistory && history.length > 0 && (
            <Typography sx={{ fontSize: "0.72rem", color: t.textMuted }}>
              {history.length} transaç{history.length === 1 ? "ão" : "ões"}
            </Typography>
          )}
        </Box>

        {loadingHistory ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={52}
                sx={{ borderRadius: "12px", bgcolor: t.skeletonBg }}
              />
            ))}
          </Box>
        ) : history.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: `1px dashed ${t.border}`,
              background: t.surfaceAlt,
              p: 4,
              textAlign: "center",
            }}
          >
            <CoinIcon size={32} />
            <Typography
              sx={{ color: t.textMuted, fontSize: "0.85rem", mt: 1.5 }}
            >
              Nenhuma transação ainda
            </Typography>
            <Typography
              sx={{ color: t.textMuted, fontSize: "0.72rem", mt: 0.4 }}
            >
              Seu bônus de cadastro e futuras compras aparecerão aqui
            </Typography>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: `1px solid ${t.border}`,
              background: t.surface,
              overflow: "hidden",
            }}
          >
            {history.map((tx, idx) => (
              <Box
                key={tx.id ?? idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  px: { xs: 2, sm: 3 },
                  py: 1.75,
                  borderBottom:
                    idx < history.length - 1 ? `1px solid ${t.border}` : "none",
                  transition: "background 0.15s",
                  "&:hover": { background: t.surfaceAlt },
                }}
              >
                {/* Date */}
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: t.textMuted,
                    minWidth: { xs: 68, sm: 90 },
                    flexShrink: 0,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {new Date(tx.created_at).toLocaleDateString("pt-BR")}
                </Typography>

                {/* Type badge */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TypeBadge type={tx.type} isDark={isDark} />
                  {tx.reason && (
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        color: t.textMuted,
                        mt: 0.2,
                        ml: 0.25,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {tx.reason}
                    </Typography>
                  )}
                </Box>

                {/* Amount */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    flexShrink: 0,
                  }}
                >
                  <CoinIcon size={16} />
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 800,
                      color: isDark ? "#4ade80" : "#16a34a",
                      letterSpacing: "-0.01em",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    +{formatNumber(tx.amount)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Paper>
        )}
      </Box>

      {/* ── Error Snackbar ───────────────────────────────────────────────────── */}
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={5000}
        onClose={() => setErrorMsg(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorMsg(null)}
          severity="error"
          variant="filled"
          sx={{ borderRadius: "12px" }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
